import { useEffect, useState } from 'react';

import { LOCAL_STORAGE } from '@/constants';
import { useIndexedDB } from '@/hooks/useIndexedDB';
import { useOrganization } from '@/hooks/useOrganization';
import { AdditionalCategory, CachedAdditionalCategory } from '@/types/DataModel';

import { useAdditionalCategory } from '../queries';

export const categoryMatchesRequestedCode = (
    category: Pick<AdditionalCategory, 'code' | 'name' | 'id'> | undefined,
    requestedCode?: string,
) => {
    if (!category || !requestedCode) {
        return false;
    }
    const wanted = requestedCode.toLowerCase();
    return [category.code, category.name, category.id]
        .some((value) => value != null && String(value).toLowerCase() === wanted);
};

const useCachedAdditionalCategory = (additionalCategoryCode?: string, additionalDataModelName?: string, extension?: string) => {

    const lookupLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);
    const { organization } = useOrganization();
    const [isCached, setIsCached] = useState(true);
    const [isLoading, setIsLoading] = useState(additionalCategoryCode ? true : false);
    const [cachedAdditionalCategory, setCachedAdditionalCategory] = useState<CachedAdditionalCategory | undefined>(undefined);

    const { data: additionalCategory, isError } = useAdditionalCategory({
        variables: {
            dataModel: additionalDataModelName ?? '',
            extension: extension ?? '',
            leaf: true,
            query: additionalCategoryCode,
            limit: 1,
        },
        enabled: additionalCategoryCode !== undefined && !isCached
    });

    const {
        add: addAdditionalCategoryToCache,
        get: getAdditionalCategoryFromCache,
        deleteAllByIndex: deleteAdditionalCategoryFromCache
    } = useIndexedDB<CachedAdditionalCategory>('additionalCategories');

    useEffect(() => {
        let cancelled = false;

        const loadCachedAdditionalCategory = async () => {
            // Drop the previous row's label immediately. Hitlist cells are
            // reused while scrolling, and a late cache read must not paint
            // that label onto the next item.
            setCachedAdditionalCategory(undefined);
            setIsLoading(Boolean(additionalCategoryCode));
            setIsCached(true);

            if (!organization || !additionalDataModelName || !additionalCategoryCode) {
                setIsLoading(false);
                return;
            }

            const requestedCode = additionalCategoryCode;
            const cacheKey = [additionalDataModelName, requestedCode, organization.dataModelHash, lookupLanguage || 'en'];
            const cached = await getAdditionalCategoryFromCache(cacheKey);

            if (cancelled || requestedCode !== additionalCategoryCode) {
                return;
            }

            if (!cached
                || cached.dataModelHash !== organization.dataModelHash
                || !categoryMatchesRequestedCode(cached, requestedCode)) {
                setIsCached(false);
            } else {
                setCachedAdditionalCategory(cached);
                setIsLoading(false);
            }
        };

        loadCachedAdditionalCategory();

        return () => {
            cancelled = true;
        };

        //eslint-disable-next-line
    }, [additionalCategoryCode, lookupLanguage]);

    useEffect(() => {
        if (isError) {
            setIsLoading(false);
        }
    }, [isError]);

    useEffect(() => {
        let cancelled = false;

        const cacheAdditionalCategory = async () => {

            if (!additionalCategory || !organization || !additionalDataModelName || !additionalCategoryCode) {
                return;
            }

            if (!categoryMatchesRequestedCode(additionalCategory, additionalCategoryCode)) {
                setIsLoading(false);
                return;
            }

            const requestedCode = additionalCategoryCode;
            const cacheKey = [additionalDataModelName, requestedCode, organization.dataModelHash, lookupLanguage || 'en'];
            const cached = {
                ...additionalCategory,
                dataModelHash: organization.dataModelHash,
                additionalModule: additionalDataModelName,
                additionalCategoryNameOrCode: requestedCode,
                translatedLanguage: lookupLanguage || 'en'
            };

            await deleteAdditionalCategoryFromCache('additionalCategoryNameOrCode', requestedCode);
            await addAdditionalCategoryToCache(cacheKey, cached);

            if (cancelled) {
                return;
            }

            setCachedAdditionalCategory(cached);
            setIsLoading(false);
        };

        cacheAdditionalCategory();

        return () => {
            cancelled = true;
        };

        //eslint-disable-next-line
    }, [additionalCategory, additionalCategoryCode, lookupLanguage]);

    return {
        isLoading,
        cachedAdditionalCategory,
    };
};

export { useCachedAdditionalCategory };
